import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps } from "../../types/screensTypes";
import {
    getWaitingAudioAI,
    createProjectAI,
    addItemToProjectAI,
    createTextRemixQueueAI,
    updateDraftItem,
    updateItemFromProject,
} from "../../api";
import { getBrandVoices, BrandVoice } from "../../api/brandVoice";
import { getAudienceProfiles, AudienceProfile } from "../../api/audienceProfile";
import AddToProjectModal from "../../components/modals/AddToProjectModal";
import AddToProjectButton from "../../components/ui/AddToProjectButton";
import Button from "../../components/ui/Button";
import { useGlobalState } from "../../context/useGlobalState";
import AIGenerationStatusModal from "../../components/modals/AIGenerationStatusModal";
import useCancelGeneration from "../../hooks/useCancelGeneration";
import { AI_MODEL_TYPES } from "../../constants";
import { Tooltip } from "@mui/material";
import { useRemainingTokens } from "../../hooks/useRemainingTokens";
import { isQuotaError } from "../../utils/quotaError";
import { FieldErrorsRemix } from "../../types/types";
import Icon from "../../components/common/Icon";
import ComposerSelect from "./ComposerSelect";
import styles from "./TextRemix.module.css";

const COLLAPSED_FIELD_H = 40;
const SRC_EXPANDED_MIN_H = 180;
const INSTR_EXPANDED_MIN_H = 140;
const RESULT_MIN_H = 120;

/** Editorial frame actually applied to the displayed remix, rebuilt from the queue entry. */
interface AppliedFrame {
    audienceName: string | null;
    writingStyleName: string | null;
    instructions: string | null;
}

const EMPTY_APPLIED_FRAME: AppliedFrame = {
    audienceName: null,
    writingStyleName: null,
    instructions: null,
};

/** Text handed over from a project page, used as the source of a new remix. */
interface RemixSource {
    name: string;
    text: string;
    language: string | null;
}

/** The language is required here too; the shared type also serves the cartel and newsletter remixes. */
type TextRemixFieldErrors = FieldErrorsRemix & { language: boolean };

const UNTOUCHED_FIELDS: TextRemixFieldErrors = {
    textTitle: false,
    textValue: false,
    promptForRemix: false,
    language: false,
};

const MaximizeIcon: React.FC = () => (
    <svg className="ic" viewBox="0 0 24 24" aria-hidden>
        <path
            d="M8 3H5a2 2 0 0 0-2 2v3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M21 8V5a2 2 0 0 0-2-2h-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M3 16v3a2 2 0 0 0 2 2h3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M16 21h3a2 2 0 0 0 2-2v-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const MinimizeIcon: React.FC = () => (
    <svg className="ic" viewBox="0 0 24 24" aria-hidden>
        <path
            d="M8 3v3a2 2 0 0 1-2 2H3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M21 8h-3a2 2 0 0 1-2-2V3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M3 16h3a2 2 0 0 1 2 2v3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M16 21v-3a2 2 0 0 1 2-2h3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const resizeComposerField = (
    el: HTMLTextAreaElement | null,
    expanded: boolean,
    maxVhRatio: number,
    expandedMinH: number,
) => {
    if (!el) return;
    el.style.height = "auto";
    if (expanded) {
        const cap = Math.round(window.innerHeight * maxVhRatio);
        const target = Math.max(el.scrollHeight, expandedMinH);
        el.style.height = `${Math.min(target, cap)}px`;
    } else {
        el.style.height = `${Math.min(Math.max(el.scrollHeight, COLLAPSED_FIELD_H), COLLAPSED_FIELD_H)}px`;
    }
};

const TextRemix: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    // Text sent from "My projects", used as the source of the first remix of this visit.
    const remixSourceRef = useRef<RemixSource | null>(
        (location.state as { remixSource?: RemixSource } | null)?.remixSource || null,
    );
    const [textValue, setTextValue] = useState<string>("");
    const [textTitle, setTextTitle] = useState<string | null>("");
    const [promptForRemix, setPromptForRemix] = useState<string | null>("");
    const [textRemix, setTextRemix] = useState<string | null>("");
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [languageCode, setLanguageCode] = useState<string | null>("");
    const [loadingTTSGeneration, setLoadingTTSGeneration] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState<boolean>(true);
    const [fieldErrors, setFieldErrors] = useState<TextRemixFieldErrors>(UNTOUCHED_FIELDS);
    // A missing field only turns red once the user has left it, as in the other forms.
    const [touched, setTouched] = useState<TextRemixFieldErrors>(UNTOUCHED_FIELDS);
    const [waitingRank, setWaitingRank] = useState<number | null>(null);
    const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
    const [generationStatus, setGenerationStatus] = useState<"waiting" | "success" | "error">("waiting");
    const [errorReason, setErrorReason] = useState<"generic" | "quota">("generic");
    // Kept as the id, not a boolean: saving an edit goes through the project endpoint
    // when the remix is attached to one, and through the draft endpoint otherwise.
    const [projectAIId, setProjectAIId] = useState<string | null>(null);
    const [srcExpanded, setSrcExpanded] = useState(false);
    const [instrExpanded, setInstrExpanded] = useState(false);
    const [srcCollapsed, setSrcCollapsed] = useState(false);
    const [audienceProfiles, setAudienceProfiles] = useState<AudienceProfile[]>([]);
    const [brandVoices, setBrandVoices] = useState<BrandVoice[]>([]);
    const [audienceProfileId, setAudienceProfileId] = useState<string | null>(null);
    const [brandVoiceId, setBrandVoiceId] = useState<string | null>(null);
    const [appliedFrame, setAppliedFrame] = useState<AppliedFrame>(EMPTY_APPLIED_FRAME);
    // A remix creates its own audioAI: only that record may be renamed from here, never the
    // source capsule we land on when arriving from the text generation step.
    const [isRemixRecord, setIsRemixRecord] = useState<boolean>(false);
    // Name as stored, shown frozen in the card: editing the composer title must not move it.
    const [capsuleName, setCapsuleName] = useState<string | null>(null);
    const [textSaveState, setTextSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
    // Last stored body, so an edit is only pushed when it actually changed.
    const savedTextRef = useRef<string>("");
    /**
     * Remix launched from this page, until its outcome is shown. Opening a remix any other way
     * (drafts, browser back) must not pop the status modal for a generation that is long over.
     */
    const launchedRemixIdRef = useRef<string | null>(null);
    const srcRef = useRef<HTMLTextAreaElement>(null);
    const instrRef = useRef<HTMLTextAreaElement>(null);
    const resultRef = useRef<HTMLTextAreaElement>(null);
    const { isTextExhausted, refetch: refetchTokens, data: tokenData } = useRemainingTokens();
    const { cancelling, cancelGeneration, confirmCanceled, resetCancelling } = useCancelGeneration();

    const languageOptions = useMemo(
        () =>
            [...(data?.languagesAI || [])]
                .map((lang) => ({ id: lang.value, name: lang.label || lang.code }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [data?.languagesAI],
    );

    /** Language as stored on the capsule, resolved from its code: the composer holds ids. */
    const capsuleLanguageName = useMemo(() => {
        if (!languageCode) {
            return null;
        }
        const match = (data?.languagesAI || []).find(
            (lang) => lang.code.toUpperCase() === (languageCode || "").toUpperCase(),
        );
        return match ? match.label || match.code : null;
    }, [data?.languagesAI, languageCode]);

    const growSrc = useCallback(
        () => resizeComposerField(srcRef.current, srcExpanded, 0.6, SRC_EXPANDED_MIN_H),
        [srcExpanded],
    );
    const growInstr = useCallback(
        () => resizeComposerField(instrRef.current, instrExpanded, 0.4, INSTR_EXPANDED_MIN_H),
        [instrExpanded],
    );
    /** The result card hugs its content (remix-b); `.rxCard` max-height clamps it to the stream. */
    const growResult = useCallback(() => resizeComposerField(resultRef.current, true, 0.9, RESULT_MIN_H), []);

    useEffect(() => {
        growSrc();
    }, [growSrc, textValue, srcCollapsed]);

    useEffect(() => {
        growInstr();
    }, [growInstr, promptForRemix]);

    useEffect(() => {
        growResult();
    }, [growResult, textRemix]);

    useEffect(() => {
        const onResize = () => {
            growSrc();
            growInstr();
            growResult();
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [growSrc, growInstr, growResult]);

    useEffect(() => {
        const fetchFrameReferentials = async () => {
            try {
                const [profiles, voices] = await Promise.all([getAudienceProfiles(), getBrandVoices()]);
                setAudienceProfiles(profiles);
                setBrandVoices(voices);
            } catch (err) {
                console.error("Error fetching audience profiles or brand voices:", err);
            }
        };

        fetchFrameReferentials();
    }, []);

    const handleGenerateRemix = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }

        // The button is disabled while a field is missing; this only guards a stale click.
        if (isGenerateButtonDisabled || !textTitle || !selectedContentLanguage) {
            return;
        }

        setLoadingTTSGeneration(true);
        setErrorReason("generic");

        try {
            const response = await createTextRemixQueueAI({
                token: token,
                textTitle: textTitle,
                textObject: textValue,
                remixPrompt: promptForRemix || "",
                language: selectedContentLanguage,
                brandVoiceId: brandVoiceId,
                audienceProfileId: audienceProfileId,
            });

            setLoadingTTSGeneration(false);
            if (!response.id) {
                console.error("Error generating text");
                setShowStatusModal(true);
                setGenerationStatus("error");
            } else {
                setTextRemix("");
                setAppliedFrame(EMPTY_APPLIED_FRAME);
                launchedRemixIdRef.current = response.id;
                navigate("/create/text-remix/" + response.id);
            }
        } catch (err) {
            setLoadingTTSGeneration(false);
            if (isQuotaError(err)) {
                setErrorReason("quota");
                refetchTokens();
            }
            setShowStatusModal(true);
            setGenerationStatus("error");
        }
    };

    /**
     * Only the result card stands for the stored capsule, so it is the only field written back.
     * Composer fields describe the next remix and must never touch an existing capsule.
     * The audioAI is reached either as a draft or as a project item, hence the two endpoints.
     */
    const persistRemixText = async () => {
        const text = textRemix || "";
        const token = localStorage.getItem("token");
        // An empty body is ignored server-side, so it is not worth a request.
        if (!token || !id || !isRemixRecord || text.trim() === "" || text === savedTextRef.current) {
            return;
        }

        setTextSaveState("saving");

        try {
            if (projectAIId) {
                await updateItemFromProject({ token, projectId: projectAIId, audioAIId: id, text });
            } else {
                await updateDraftItem({ token, itemId: id, itemType: "audio", text });
            }
            savedTextRef.current = text;
            setTextSaveState("saved");
        } catch (error) {
            console.error("Error saving the remixed text:", error);
            setTextSaveState("error");
        }
    };

    const markTouched = useCallback((field: keyof TextRemixFieldErrors) => {
        setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
    }, []);
    const markLanguageTouched = useCallback(() => markTouched("language"), [markTouched]);

    useEffect(() => {
        setTouched(UNTOUCHED_FIELDS);
        if (id && id.trim() !== "") {
            // Once a remix exists, a later new remix starts blank, not from the project text.
            remixSourceRef.current = null;
            handleAudioAIAwaiting(id);
        } else {
            setTextTitle("");
            setTextValue("");
            setPromptForRemix("");
            setAudienceProfileId(null);
            setBrandVoiceId(null);
            setAppliedFrame(EMPTY_APPLIED_FRAME);
            setSrcCollapsed(false);
            setIsRemixRecord(false);
            setProjectAIId(null);
            setCapsuleName(null);
            setTextSaveState("idle");
            savedTextRef.current = "";

            // Applied on every blank run (StrictMode runs this effect twice), cleared above.
            const source = remixSourceRef.current;
            if (source) {
                setTextTitle(source.name);
                setTextValue(source.text);
                // Resolved to a language id by the languageCode effect below.
                setLanguageCode(source.language || "");
            }
        }
        // fetch-on-mount when id changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleAudioAIAwaiting = async (id: string) => {
        // The modal and the outcome (success, failure, canceled) only concern a remix launched
        // from this page, or one still running when the page opens.
        let showOutcome = launchedRemixIdRef.current === id;
        const concludeLaunch = () => {
            if (launchedRemixIdRef.current === id) launchedRemixIdRef.current = null;
        };

        setLoadingTTSGeneration(true);
        setShowStatusModal(showOutcome);
        setGenerationStatus("waiting");
        setWaitingRank(null);
        // Nothing is known about the record being loaded yet.
        setCapsuleName(null);

        const fetchAudioAI = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    return;
                }

                const audioAI = await getWaitingAudioAI({
                    token: token,
                    id,
                });

                if (!audioAI || !audioAI.audioAI) {
                    console.error("Unexpected response format:", audioAI);
                    setLoadingTTSGeneration(false);
                    return;
                }

                if (audioAI?.failed) {
                    setLoadingTTSGeneration(false);
                    setGenerationStatus("error");
                    resetCancelling();
                    concludeLaunch();
                    return;
                }

                const { waiting, audioAI: audio, queueAI: queue } = audioAI;

                if (queue && queue.queueAIRemixId && queue.queueAIRemix) {
                    setTextValue(queue.queueAIRemix.textObject);
                }

                const text = audio?.text || "";

                if (text.trim() && !(queue && queue.queueAIRemixId)) {
                    setTextValue(text);
                    setShowStatusModal(false);
                } else if (text.trim() && queue && queue.queueAIRemixId) {
                    setTextRemix(text);
                    savedTextRef.current = text;
                    setSrcCollapsed(true);
                }

                setIsRemixRecord(!!(queue && queue.queueAIRemixId));

                if (queue) {
                    if (audio) {
                        setTextTitle(audio.name);
                        setCapsuleName(audio.name || null);
                        const languageCode = audio.language;
                        setLanguageCode(languageCode);
                        setProjectAIId(audio.projectAIId || null);
                    }

                    if (queue && queue.queueAIRemix) {
                        const remix = queue.queueAIRemix;
                        setPromptForRemix(remix.promptRemix);
                        setAudienceProfileId(remix.audienceProfileId || null);
                        setBrandVoiceId(remix.brandVoiceId || null);
                        setAppliedFrame({
                            audienceName: remix.audienceProfile?.name || null,
                            writingStyleName: remix.brandVoice?.name || null,
                            instructions: remix.promptRemix?.trim() || null,
                        });
                    }
                }

                // Checked after the composer is restored from the queue, so the remix can be
                // relaunched as is, even after a reload.
                if (audioAI.canceled) {
                    setLoadingTTSGeneration(false);
                    setShowStatusModal(false);
                    setWaitingRank(null);
                    if (showOutcome) confirmCanceled();
                    else resetCancelling();
                    concludeLaunch();
                    return;
                }

                if (waiting || (queue && !queue.queueAIRemixId)) {
                    if (queue && !queue.queueAIRemixId) setLoadingTTSGeneration(false);

                    if (queue && queue.queueAIRemixId) {
                        // Still running: from here on, its outcome is worth showing.
                        if (!showOutcome) {
                            showOutcome = true;
                            setShowStatusModal(true);
                        }
                        if (typeof audioAI.rank === "number") {
                            setWaitingRank(audioAI.rank);
                        }
                        setTimeout(fetchAudioAI, 2000);
                    }
                } else {
                    setGenerationStatus("success");
                    setLoadingTTSGeneration(false);
                    setWaitingRank(null);
                    refetchTokens();
                    resetCancelling();
                    concludeLaunch();
                }
            } catch (error) {
                console.error("Error fetching audio AI data:", error);
                setLoadingTTSGeneration(false);
                // A record that cannot be read is worth reporting however the page was opened.
                setShowStatusModal(true);
                setGenerationStatus("error");
                resetCancelling();
                concludeLaunch();
            }
        };

        fetchAudioAI();
    };

    const handleAddToProject = async (projectId: string, newProjectName: string) => {
        const token = localStorage.getItem("token");
        if (!token || !id) {
            return;
        }

        const audioAI = await getWaitingAudioAI({
            token: token,
            id: id,
        });

        if (audioAI) {
            const audioAIData = audioAI;
            const audio = audioAIData.audioAI;

            if (projectId === "new") {
                await createProjectAI({
                    token: token,
                    name: newProjectName,
                    audioAIId: audio.id,
                });
            } else {
                await addItemToProjectAI({
                    token: token,
                    projectId,
                    itemId: audio.id,
                    itemType: AI_MODEL_TYPES.AUDIO,
                });
            }

            await refreshProjects();
            // Refresh the audioAI to check if it's now in a project
            if (id) {
                const updatedAudioAI = await getWaitingAudioAI({ token: token, id: id });
                if (updatedAudioAI?.audioAI) {
                    setProjectAIId(updatedAudioAI.audioAI.projectAIId || null);
                }
            }
        }
    };

    useEffect(() => {
        // An audience or a writing style is a valid frame on its own: instructions become optional.
        const errors: TextRemixFieldErrors = {
            textTitle: !textTitle,
            textValue: !textValue,
            promptForRemix: !promptForRemix && !audienceProfileId && !brandVoiceId,
            language: !selectedContentLanguage,
        };

        setFieldErrors(errors);
        setIsGenerateButtonDisabled(Object.values(errors).some((error) => error));
    }, [textValue, textTitle, promptForRemix, audienceProfileId, brandVoiceId, selectedContentLanguage]);

    useEffect(() => {
        if (data && data.languagesAI && data.languagesAI.length > 0 && languageCode) {
            const languageAI = data.languagesAI;

            const item = languageAI.find((o) => o.code.toUpperCase() === languageCode.toUpperCase());

            const languageId = item?.value;

            if (languageId) {
                setSelectedContentLanguage(languageId);
            }
        }
    }, [languageCode, data]);

    const handleNextStep = () => {
        // A remix that produced nothing (canceled or failed) left behind an AudioAI with an empty
        // text: opening it by id would show nothing. Hand the source text over instead, the way
        // the drafts send texts to Text-to-Speech.
        if (isRemixRecord && !textRemix) {
            navigate("/create/text-to-speech", {
                state: {
                    importedCapsules: [{ name: textTitle || "", text: textValue, language: languageCode || null }],
                },
            });
            return;
        }
        navigate(`/create/text-to-speech/${id || ""}`);
    };

    const remainingWords = tokenData?.remainigTextTokens;
    // Named in the send button tooltip, in the order the fields appear.
    const missingFieldLabels = [
        fieldErrors.textTitle && t("title"),
        fieldErrors.textValue && t("remixSourceLabel"),
        fieldErrors.promptForRemix && t("instructions"),
        fieldErrors.language && t("language"),
    ].filter(Boolean);
    const sendTooltip = isTextExhausted
        ? t("quotaReachedTextTooltip")
        : isGenerateButtonDisabled
          ? t("formIncompleteTooltip", { fields: missingFieldLabels.join(", ") })
          : "";
    const hasAppliedFrame = !!(appliedFrame.audienceName || appliedFrame.writingStyleName || appliedFrame.instructions);
    // Read-only chips, shown next to the frame badges on the same row.
    const hasCapsuleIdentity = isRemixRecord && !!(capsuleName || capsuleLanguageName);
    // The card stands for a remix output only: a text brought over from the generation
    // step lands in the composer source field, not here.
    const showResultCard = !!textRemix || loadingTTSGeneration;
    // A text brought over from the generation step can still move on to the next step.
    const hasReusableText = !!id && !!(textRemix || textValue);
    // Filing into a project only makes sense for a remix this page actually produced.
    const hasRemixResult = isRemixRecord && !!textRemix;

    return (
        <div className={styles.page}>
            <section className={styles.main}>
                <div className={styles.mainHead}>
                    <div>
                        <h1>{t("textRemix")}</h1>
                        <p>{t("textRemixPageSubtitle")}</p>
                    </div>
                    {remainingWords != null && (
                        <span className={styles.token}>
                            <Icon name="ic-sparkles" />
                            {t("remainingWords")}&nbsp;: <b>{remainingWords.toLocaleString()}</b>
                        </span>
                    )}
                </div>

                <div className={styles.stream}>
                    <div className={`${styles.streamInner}${showResultCard ? ` ${styles.streamInnerResult}` : ""}`}>
                        {!showResultCard ? (
                            <div className={styles.empty}>
                                <div className={styles.emptyIco}>
                                    <Icon name="ic-shuffle" />
                                </div>
                                <h3>{t("yourRemixedTextWillAppearHere")}</h3>
                                <p>{t("remixEmptySubtitle")}</p>
                            </div>
                        ) : (
                            <div className={styles.rxCard}>
                                <div className={styles.rxHead}>
                                    <Icon name="ic-shuffle" />
                                    {t("remixedText")}
                                    {textSaveState !== "idle" && (
                                        <span
                                            className={`${styles.rxSave}${textSaveState === "error" ? ` ${styles.rxSaveError}` : ""}`}
                                        >
                                            {textSaveState === "saving" && t("saving")}
                                            {textSaveState === "saved" && t("savedChanges")}
                                            {textSaveState === "error" && t("saveChangesFailed")}
                                        </span>
                                    )}
                                </div>
                                {(hasCapsuleIdentity || hasAppliedFrame) && (
                                    <div className={styles.rxApplied}>
                                        {hasCapsuleIdentity && capsuleName && (
                                            <Tooltip title={capsuleName} placement="top">
                                                <span className={`${styles.rxChip} ${styles.rxChipMuted}`}>
                                                    <Icon name="ic-file-text" />
                                                    <span className={styles.rxChipText}>{capsuleName}</span>
                                                </span>
                                            </Tooltip>
                                        )}
                                        {hasCapsuleIdentity && capsuleLanguageName && (
                                            <span className={`${styles.rxChip} ${styles.rxChipMuted}`}>
                                                <Icon name="ic-languages" />
                                                {capsuleLanguageName}
                                            </span>
                                        )}
                                        {appliedFrame.audienceName && (
                                            <span className={styles.rxChip}>
                                                <Icon name="ic-users" />
                                                {appliedFrame.audienceName}
                                            </span>
                                        )}
                                        {appliedFrame.writingStyleName && (
                                            <span className={styles.rxChip}>
                                                <Icon name="ic-pencil" />
                                                {appliedFrame.writingStyleName}
                                            </span>
                                        )}
                                        {appliedFrame.instructions && (
                                            <Tooltip title={appliedFrame.instructions} placement="top">
                                                <span
                                                    className={`${styles.rxChip} ${styles.rxChipMuted} ${styles.rxChipInstr}`}
                                                >
                                                    <Icon name="ic-shuffle" />
                                                    {t("remixAppliedInstructions")}
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                                <textarea
                                    ref={resultRef}
                                    className={styles.rxBody}
                                    value={textRemix || ""}
                                    onChange={(e) => {
                                        setTextRemix(e.target.value);
                                        // Typing again invalidates the previous outcome.
                                        setTextSaveState("idle");
                                    }}
                                    onBlur={persistRemixText}
                                    placeholder={
                                        loadingTTSGeneration ? t("generating") : t("yourRemixedTextWillAppearHere")
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.composerWrap}>
                    <div className={styles.composerHintTop}>
                        <Icon name="ic-info" />
                        {t("remixFrameHint")}
                    </div>
                    <div className={styles.composer}>
                        <div className={styles.titleRow}>
                            <input
                                className={`${styles.titleInput}${touched.textTitle && fieldErrors.textTitle ? ` ${styles.titleInputMissing}` : ""}`}
                                placeholder={t("remixTitlePlaceholder")}
                                value={textTitle || ""}
                                onChange={(e) => setTextTitle(e.target.value)}
                                onBlur={() => markTouched("textTitle")}
                            />
                        </div>
                        {srcCollapsed ? (
                            <button type="button" className={styles.srcToggle} onClick={() => setSrcCollapsed(false)}>
                                <span className={styles.srcToggleLead}>
                                    <Icon name="ic-file-text" />
                                    {t("editSourceText")}
                                </span>
                                <Icon name="ic-chevron-down" className={styles.srcToggleChevron} />
                            </button>
                        ) : (
                            <div className={styles.fieldWrap}>
                                <span
                                    className={`${styles.fieldLabel}${touched.textValue && fieldErrors.textValue ? ` ${styles.fieldLabelMissing}` : ""}`}
                                >
                                    <Icon name="ic-file-text" />
                                    {t("remixSourceLabel")}
                                </span>
                                <button
                                    type="button"
                                    className={styles.expandBtn}
                                    title={srcExpanded ? t("reduce") || "Réduire" : t("expand") || "Agrandir"}
                                    aria-expanded={srcExpanded}
                                    onClick={() => setSrcExpanded((v) => !v)}
                                >
                                    {srcExpanded ? <MinimizeIcon /> : <MaximizeIcon />}
                                </button>
                                <textarea
                                    ref={srcRef}
                                    className={`${styles.fieldSrc}${srcExpanded ? ` ${styles.fieldSrcExpanded}` : ""}`}
                                    placeholder={t("remixSourcePlaceholder")}
                                    value={textValue}
                                    rows={1}
                                    onChange={(e) => setTextValue(e.target.value)}
                                    onBlur={() => markTouched("textValue")}
                                />
                            </div>
                        )}
                        <div className={styles.fieldWrap}>
                            <span
                                className={`${styles.fieldLabel}${touched.promptForRemix && fieldErrors.promptForRemix ? ` ${styles.fieldLabelMissing}` : ""}`}
                            >
                                <Icon name="ic-shuffle" />
                                {t("instructions")}
                            </span>
                            <button
                                type="button"
                                className={styles.expandBtn}
                                title={instrExpanded ? t("reduce") || "Réduire" : t("expand") || "Agrandir"}
                                aria-expanded={instrExpanded}
                                onClick={() => setInstrExpanded((v) => !v)}
                            >
                                {instrExpanded ? <MinimizeIcon /> : <MaximizeIcon />}
                            </button>
                            <textarea
                                ref={instrRef}
                                className={`${styles.fieldPrompt}${instrExpanded ? ` ${styles.fieldPromptExpanded}` : ""}`}
                                placeholder={t("remixInstructionsPlaceholder")}
                                value={promptForRemix || ""}
                                rows={1}
                                onChange={(e) => setPromptForRemix(e.target.value)}
                                onBlur={() => markTouched("promptForRemix")}
                            />
                        </div>
                        <div className={styles.composerBar}>
                            <ComposerSelect
                                icon="ic-users"
                                label={t("audienceProfile")}
                                placeholder={t("noAudience")}
                                noneLabel={t("noAudience")}
                                options={audienceProfiles.map((profile) => ({
                                    id: profile.id,
                                    name: profile.name,
                                }))}
                                value={audienceProfileId}
                                onChange={setAudienceProfileId}
                                manageLabel={t("manageAudienceProfiles")}
                                onManage={() => navigate("/resources/audience-profiles")}
                            />
                            <ComposerSelect
                                icon="ic-pencil"
                                label={t("brandVoice")}
                                placeholder={t("noVoice")}
                                noneLabel={t("noVoice")}
                                options={brandVoices.map((voice) => ({ id: voice.id, name: voice.name }))}
                                value={brandVoiceId}
                                onChange={setBrandVoiceId}
                                manageLabel={t("manageBrandVoices")}
                                onManage={() => navigate("/resources/brand-voices")}
                            />
                            <ComposerSelect
                                icon="ic-languages"
                                label={t("language")}
                                placeholder={t("selectALanguage")}
                                options={languageOptions}
                                value={selectedContentLanguage}
                                onChange={setSelectedContentLanguage}
                                variant="tool"
                                searchable
                                searchPlaceholder={t("search")}
                                invalid={touched.language && fieldErrors.language}
                                onClose={markLanguageTouched}
                            />
                            <span className={styles.spacer} />
                            <Tooltip title={sendTooltip} placement="top">
                                <span>
                                    <button
                                        type="button"
                                        className={styles.send}
                                        title={t("generateRemix")}
                                        onClick={handleGenerateRemix}
                                        disabled={isGenerateButtonDisabled || loadingTTSGeneration || isTextExhausted}
                                    >
                                        <Icon name="ic-arrow-right" />
                                    </button>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                    <div className={styles.actions}>
                        {hasReusableText && <Button label={t("nextStep")} onClick={handleNextStep} variant="ghost" />}
                        {hasRemixResult && !projectAIId && <AddToProjectButton onClick={() => setIsModalOpen(true)} />}
                    </div>
                </div>
            </section>

            <AddToProjectModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAddToProject={handleAddToProject}
                placeholder={t("addAudioToProject")}
            />

            <AIGenerationStatusModal
                isOpen={showStatusModal}
                onClose={() => setShowStatusModal(false)}
                status={generationStatus}
                rank={waitingRank}
                errorTitle={errorReason === "quota" ? t("quotaReachedTitle") : undefined}
                errorSubtitle={errorReason === "quota" ? t("quotaReachedTextSubtitle") : undefined}
                onCancel={id ? () => cancelGeneration(id) : undefined}
                cancelling={cancelling}
            />
        </div>
    );
};

export default TextRemix;
